import { Link } from 'react-router-dom';
import type { Post } from '../lib/types';
import { formatPostDate } from '../lib/dateFormat';

export function HeroRail({ posts }: { posts: Post[] }) {
  const primary = posts[0];
  const secondary = posts[1];

  if (!primary) return null;

  return (
    <section className="hero-rail">
      <article className="hero-card primary">
        <Link
          to={`/posts/${primary.slug}`}
          className="hero-media"
          aria-label={`Read ${primary.title}`}
        >
          <img
            src={
              primary.header_image_url ||
              'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1400&q=80'
            }
            alt={primary.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Link>
        <div className="hero-copy">
          <div className="eyebrow">{primary.category || 'Featured'}</div>
          <h2 className="hero-title">
            <Link to={`/posts/${primary.slug}`}>{primary.title}</Link>
          </h2>
          <p className="hero-excerpt">{primary.excerpt}</p>
          <div className="meta-row">
            <span className="byline">{primary.author?.name || 'Unknown'}</span>
            <span className="dot-sep" />
            <span>{formatPostDate(primary.date_published || primary.created_at)}</span>
          </div>
        </div>
      </article>

      {secondary ? (
        <article className="hero-card secondary">
          <Link
            to={`/posts/${secondary.slug}`}
            className="hero-media"
            aria-label={`Read ${secondary.title}`}
          >
            <img
              src={
                secondary.header_image_url ||
                'https://images.unsplash.com/photo-1505685296765-3a2736de412f?auto=format&fit=crop&w=1100&q=80'
              }
              alt={secondary.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Link>
          <div className="hero-copy">
            <div className="eyebrow">{secondary.category || 'Spotlight'}</div>
            <h3 className="hero-title">
              <Link to={`/posts/${secondary.slug}`}>{secondary.title}</Link>
            </h3>
            <div className="meta-row">
              <span className="byline">{secondary.author?.name || 'Unknown'}</span>
              <span className="dot-sep" />
              <span>{formatPostDate(secondary.date_published || secondary.created_at)}</span>
            </div>
          </div>
        </article>
      ) : null}
    </section>
  );
}

type PostGridProps = {
  posts: Post[];
  title: string;
  ctaHref?: string;
  ctaLabel?: string;
};

export function PostGrid({ posts, title, ctaHref, ctaLabel = 'View all' }: PostGridProps) {
  if (posts.length === 0) {
    return <p className="placeholder">No posts yet - the first n8n publish will populate this feed.</p>;
  }

  return (
    <section className="stack">
      <div className="section-header">
        <h3>{title}</h3>
        {ctaHref ? (
          <Link to={ctaHref} className="inline-link">
            {ctaLabel}
          </Link>
        ) : null}
      </div>
      <div className="card-grid">
        {posts.map((post) => (
          <article className="card" key={post.slug}>
            <Link
              to={`/posts/${post.slug}`}
              className="card-media"
              aria-label={`Read ${post.title}`}
            >
              <img
                src={
                  post.header_image_url ||
                  'https://images.unsplash.com/photo-1505685296765-3a2736de412f?auto=format&fit=crop&w=900&q=80'
                }
                alt={post.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Link>
            <div className="card-body">
              <div className="tag-pill">{post.category || 'General'}</div>
              <h3 className="title">
                <Link to={`/posts/${post.slug}`}>{post.title}</Link>
              </h3>
              <p className="excerpt">{post.excerpt}</p>
              <div className="meta-row">
                <span className="byline">{post.author?.name || 'Unknown'}</span>
                <span className="dot-sep" />
                <span>{formatPostDate(post.date_published || post.created_at)}</span>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
